import { Navigate, Outlet, NavLink, useNavigate } from "react-router-dom";
import { Loader2, LayoutDashboard, Inbox, Images, MapPin, Gauge, Users, Star, LogOut } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../context/AuthContext";
import logo from "../../assets/ruempeltaxi-logo.png";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/anfragen", label: "Anfragen", icon: Inbox },
  { to: "/admin/projekte", label: "Vorher-Nachher", icon: Images },
  { to: "/admin/bewertungen", label: "Bewertungen", icon: Star },
  { to: "/admin/orte", label: "Lokale SEO", icon: MapPin },
  { to: "/admin/performance", label: "Performance", icon: Gauge },
  { to: "/admin/mitarbeiter", label: "Mitarbeiter", icon: Users },
];

export default function AdminLayout() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();

  if (!checked || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-black">
        <Loader2 className="h-8 w-8 animate-spin text-yellow" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/mitarbeiter" replace />;

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="flex min-h-screen bg-light-gray" data-testid="admin-layout">
      <Helmet>
        <title>Admin | Rümpeltaxi</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <aside className="hidden w-64 shrink-0 flex-col border-r border-dark-gray bg-brand-black md:flex">
        <div className="border-b border-dark-gray p-6">
          <img src={logo} alt="Rümpeltaxi" className="h-8 w-auto" />
          <p className="mt-3 text-xs text-light-gray/50">Administration</p>
        </div>
        <nav className="flex-1 space-y-1 p-4">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-sm px-4 py-3 font-heading text-sm font-semibold transition-colors ${
                  isActive ? "bg-yellow text-brand-black" : "text-light-gray/70 hover:bg-dark-gray hover:text-white"
                }`
              }
              data-testid={`admin-nav-${n.label}`}
            >
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-dark-gray p-4">
          <p className="mb-3 px-4 text-xs text-light-gray/50">{user.name}</p>
          <button
            onClick={onLogout}
            className="flex w-full items-center gap-3 rounded-sm px-4 py-3 font-heading text-sm font-semibold text-light-gray/70 transition-colors hover:bg-dark-gray hover:text-white"
            data-testid="admin-logout"
          >
            <LogOut className="h-5 w-5" /> Abmelden
          </button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-dark-gray bg-brand-black px-4 py-3 md:hidden">
          <img src={logo} alt="Rümpeltaxi" className="h-7 w-auto" />
          <div className="flex items-center gap-2">
            {navItems.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `rounded-sm p-2 ${isActive ? "bg-yellow text-brand-black" : "text-light-gray/70"}`}>
                <n.icon className="h-5 w-5" />
              </NavLink>
            ))}
            <button onClick={onLogout} className="rounded-sm p-2 text-light-gray/70" data-testid="admin-logout-mobile">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
        <main className="flex-1 overflow-x-hidden p-5 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
